/**
 * Synthesised hit sounds of the procedural skin (W2-01b; ARCHITECTURE.md §12, skin-and-assets.md S§1.4, S§5):
 * click (Tap and Hold heads), drag and flick, rendered once with an OfflineAudioContext and handed to the audio
 * engine as encoded 16-bit PCM WAV, like a respack's sound files.
 *
 *  - click: a crisp tick (band-passed noise burst + a falling sine), 0.12 s;
 *  - drag: a light, high "tink", 0.1 s, quieter than click;
 *  - flick: a short upward swish (noise through a rising band-pass + a rising chirp), 0.3 s.
 *
 * Like the Phigros skin's sounds, every sound starts immediately (attack <= 1 ms, no leading silence), so no extra
 * latency compensation is needed. Noise comes from a fixed seed: the sounds are identical on every start. Each sound
 * is peak-normalised to -1 dBFS.
 */

import type { SkinSounds } from './decodeSkin';

export type SkinSoundName = 'click' | 'drag' | 'flick';

export const PROCEDURAL_SOUND_RATE = 44100;
/** Peak after normalisation (-1 dBFS). */
export const PROCEDURAL_SOUND_PEAK = 0.891;

/** mulberry32: small deterministic PRNG for the noise source. */
function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Seeded white noise in [-1, 1). */
export function noiseSamples(length: number, seed: number): Float32Array<ArrayBuffer> {
  const rnd = mulberry32(seed);
  const out = new Float32Array(length);
  for (let i = 0; i < length; i++) out[i] = rnd() * 2 - 1;
  return out;
}

interface Envelope {
  readonly peak: number;
  /** Seconds to reach the peak (linear). */
  readonly attack: number;
  /** Second at which the exponential decay reaches silence. */
  readonly end: number;
}

const SILENT = 0.0005;

function envelope(param: AudioParam, env: Envelope): void {
  param.setValueAtTime(0, 0);
  param.linearRampToValueAtTime(env.peak, env.attack);
  param.exponentialRampToValueAtTime(SILENT, env.end);
  param.linearRampToValueAtTime(0, env.end + 0.002);
}

interface Voice {
  connect(ctx: OfflineAudioContext, out: AudioNode): AudioScheduledSourceNode;
}

function noiseVoice(
  seed: number,
  filter: {
    readonly type: BiquadFilterType;
    readonly from: number;
    readonly to?: number;
    readonly at?: number;
    readonly q: number;
  },
  env: Envelope,
): Voice {
  return {
    connect(ctx, out) {
      const buf = ctx.createBuffer(1, ctx.length, ctx.sampleRate);
      buf.copyToChannel(noiseSamples(ctx.length, seed), 0);
      const src = ctx.createBufferSource();
      src.buffer = buf;
      const bq = ctx.createBiquadFilter();
      bq.type = filter.type;
      bq.Q.setValueAtTime(filter.q, 0);
      bq.frequency.setValueAtTime(filter.from, 0);
      if (filter.to !== undefined) bq.frequency.exponentialRampToValueAtTime(filter.to, filter.at ?? env.end);
      const gain = ctx.createGain();
      envelope(gain.gain, env);
      src.connect(bq).connect(gain).connect(out);
      return src;
    },
  };
}

function toneVoice(
  type: OscillatorType,
  freq: { readonly from: number; readonly to?: number; readonly at?: number },
  env: Envelope,
): Voice {
  return {
    connect(ctx, out) {
      const osc = ctx.createOscillator();
      osc.type = type;
      osc.frequency.setValueAtTime(freq.from, 0);
      if (freq.to !== undefined) osc.frequency.exponentialRampToValueAtTime(freq.to, freq.at ?? env.end);
      const gain = ctx.createGain();
      envelope(gain.gain, env);
      osc.connect(gain).connect(out);
      return osc;
    },
  };
}

interface Recipe {
  readonly duration: number;
  readonly voices: readonly Voice[];
}

/** The three sounds (see the file header). */
export const SOUND_RECIPES: Readonly<Record<SkinSoundName, Recipe>> = {
  click: {
    duration: 0.12,
    voices: [
      noiseVoice(0x51c4, { type: 'bandpass', from: 3200, q: 0.9 }, { peak: 0.9, attack: 0.0008, end: 0.05 }),
      toneVoice('sine', { from: 1900, to: 1100, at: 0.03 }, { peak: 0.55, attack: 0.0008, end: 0.09 }),
      toneVoice('triangle', { from: 3800 }, { peak: 0.12, attack: 0.0005, end: 0.025 }),
    ],
  },
  drag: {
    duration: 0.1,
    voices: [
      toneVoice('sine', { from: 2900, to: 2750, at: 0.06 }, { peak: 0.35, attack: 0.001, end: 0.06 }),
      toneVoice('sine', { from: 5800 }, { peak: 0.12, attack: 0.001, end: 0.035 }),
      noiseVoice(0xd7a9, { type: 'highpass', from: 7000, q: 0.7 }, { peak: 0.15, attack: 0.0005, end: 0.015 }),
    ],
  },
  flick: {
    duration: 0.3,
    voices: [
      noiseVoice(
        0xf11c,
        { type: 'bandpass', from: 1400, to: 7500, at: 0.2, q: 1.4 },
        { peak: 0.8, attack: 0.02, end: 0.28 },
      ),
      toneVoice('sine', { from: 1100, to: 2600, at: 0.12 }, { peak: 0.25, attack: 0.005, end: 0.15 }),
    ],
  },
};

/** Scales the channels so their peak is `target` (silence stays silent). */
export function normalizePeak(channels: readonly Float32Array[], target = PROCEDURAL_SOUND_PEAK): void {
  let peak = 0;
  for (const ch of channels) for (const v of ch) peak = Math.max(peak, Math.abs(v));
  if (peak <= 1e-9) return;
  const k = target / peak;
  for (const ch of channels) for (let i = 0; i < ch.length; i++) ch[i] = (ch[i] ?? 0) * k;
}

/** Encodes PCM channels (equal lengths, -1..1) as a 16-bit little-endian PCM WAV file. */
export function encodeWavPcm16(channels: readonly Float32Array[], sampleRate: number): Uint8Array<ArrayBuffer> {
  const nch = Math.max(1, channels.length);
  const frames = channels.reduce((n, ch) => Math.max(n, ch.length), 0);
  const blockAlign = nch * 2;
  const dataBytes = frames * blockAlign;
  const out = new Uint8Array(44 + dataBytes);
  const view = new DataView(out.buffer);
  const ascii = (at: number, s: string): void => {
    for (let i = 0; i < s.length; i++) out[at + i] = s.charCodeAt(i);
  };
  ascii(0, 'RIFF');
  view.setUint32(4, 36 + dataBytes, true);
  ascii(8, 'WAVE');
  ascii(12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true); // PCM
  view.setUint16(22, nch, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * blockAlign, true);
  view.setUint16(32, blockAlign, true);
  view.setUint16(34, 16, true);
  ascii(36, 'data');
  view.setUint32(40, dataBytes, true);
  let at = 44;
  for (let f = 0; f < frames; f++) {
    for (let c = 0; c < nch; c++) {
      const v = channels[c]?.[f] ?? 0;
      const s = Math.max(-1, Math.min(1, Number.isFinite(v) ? v : 0));
      view.setInt16(at, Math.round(s < 0 ? s * 0x8000 : s * 0x7fff), true);
      at += 2;
    }
  }
  return out;
}

/** Renders one sound with an OfflineAudioContext and encodes it as WAV. */
export async function synthesizeSkinSound(
  name: SkinSoundName,
  sampleRate = PROCEDURAL_SOUND_RATE,
): Promise<{ readonly bytes: Uint8Array; readonly ext: 'wav' }> {
  const recipe = SOUND_RECIPES[name];
  const length = Math.ceil(recipe.duration * sampleRate);
  const ctx = new OfflineAudioContext({ numberOfChannels: 1, length, sampleRate });
  const master = ctx.createGain();
  master.gain.setValueAtTime(1, 0);
  master.connect(ctx.destination);
  for (const voice of recipe.voices) {
    const src = voice.connect(ctx, master);
    src.start(0);
    src.stop(recipe.duration);
  }
  const rendered = await ctx.startRendering();
  const channels: Float32Array[] = [];
  for (let c = 0; c < rendered.numberOfChannels; c++) channels.push(new Float32Array(rendered.getChannelData(c)));
  normalizePeak(channels);
  return { bytes: encodeWavPcm16(channels, rendered.sampleRate), ext: 'wav' };
}

/** The three synthesised sounds; a sound that cannot be rendered is null (the audio engine then uses its own). */
export async function synthesizeSkinSounds(): Promise<SkinSounds> {
  if (typeof OfflineAudioContext !== 'function') return { click: null, drag: null, flick: null };
  const one = (name: SkinSoundName) => synthesizeSkinSound(name).catch(() => null);
  const [click, drag, flick] = await Promise.all([one('click'), one('drag'), one('flick')]);
  return { click, drag, flick };
}
